import React, { Fragment } from 'react';
import { scaleLinear } from 'd3-scale';
import { range } from 'lodash';
import styled from 'styled-components';
import { ErrorFilled } from '@equinor/opt-ui-icons';
import { DVColors } from 'Widgets/ColorSchemes';
import { HeatmapScaleTypes } from 'Widgets/HeatMap/constants';
import { getScale } from 'Widgets/HeatMap/utils';
import { Tooltip } from 'Widgets/TooltipMui';
const MAX_BOXES = 100;
const SCALE_DOMAIN_RANGE = range(MAX_BOXES + 1); // values for scale in 0-100 range
const HeatMapBox = styled.div `
  width: ${({ width }) => (typeof width === 'number' ? `${width}px` : width)};
  height: ${({ height }) => height}px;
  border-right: ${({ borderRight }) => borderRight};

  border-radius: 0px;
  &:hover {
    cursor: pointer;
    border-radius: 6px;
  }
  &:focus {
    border-radius: 6px;
  }
`;
const HeatMapInnerBox = styled.div `
  width: ${(props) => (props.excluded ? '105%' : '95%')};
  height: 100%;
  display: inline-block;
  vertical-align: middle;
  margin-top: ${(props) => props.marginTop || 3}px;
  margin-left: ${(props) => (props.excluded ? '0px' : '3px')};
  z-index: 10;
`;
const HeatMapCrossedLine = styled.div `
  background: linear-gradient(
    to top left,
    rgb(0, 0, 0, 0) calc(50% - 1px),
    #000,
    rgb(0, 0, 0, 0) calc(50% + 1px)
  );
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
`;
const HeatMap = ({ width = '100%', height = 42, min, max, label, labelComponent, isLoading, colorScheme = [
    '#FFFFFF',
    '#FEFC9C',
    '#FDF957',
    '#EDAB40',
    '#E25F2D',
    '#D32F23',
    '#9A1E14',
    '#040001'
], boxes, scaleFunction = getScale(HeatmapScaleTypes.LINEAR, min, max) }) => {
    const increments = MAX_BOXES / (colorScheme.length - 1);
    const colorScale = scaleLinear()
        .domain(SCALE_DOMAIN_RANGE.map((d, i) => increments * i))
        .range(colorScheme);
    const getScoreColor = (score) => {
        const scaledScore = scaleFunction(score ?? min);
        const validScaledScore = Number.isFinite(scaledScore) ? scaledScore : 0;
        return colorScale(validScaledScore);
    };
    // --- create tooltip
    const createToolTip = (index) => {
        const box = boxes[index];
        if (!box)
            return null;
        const { score, tooltipData } = box;
        if (!tooltipData)
            return null;
        return (React.createElement("div", { className: "app-chart--tooltip" },
            React.createElement("table", { className: "app-chart--tooltip-legend" },
                React.createElement("tbody", null,
                    React.createElement(Fragment, { key: `f-${index}` },
                        React.createElement("tr", { key: `start-time-${index}` },
                            React.createElement("td", { className: "app-chart--tooltip-label" },
                                React.createElement("span", null, "Start")),
                            React.createElement("td", { className: "app-chart--tooltip-value" }, tooltipData.startTime)),
                        React.createElement("tr", { key: `end-time-${index}` },
                            React.createElement("td", { className: "app-chart--tooltip-label" },
                                React.createElement("span", null, "End")),
                            React.createElement("td", { className: "app-chart--tooltip-value" }, tooltipData.endTime))))),
            React.createElement("table", { className: "app-chart--tooltip-legend" },
                React.createElement("tbody", null,
                    React.createElement("tr", null, score === null ? (React.createElement("td", { colSpan: 2, className: "app-chart--tooltip-label" },
                        React.createElement("span", null, tooltipData.title))) : (React.createElement(React.Fragment, null,
                        React.createElement("td", { className: "app-chart--tooltip-label" },
                            React.createElement("span", null, tooltipData.title)),
                        React.createElement("td", { className: "app-chart--tooltip-value" }, score)))),
                    React.createElement("tr", null, tooltipData.imputed && (React.createElement("td", { colSpan: 2, className: "app-chart--tooltip-label" },
                        React.createElement("div", { className: "app-l-flex-row app-l-flex-row__align-center" },
                            React.createElement(ErrorFilled, { size: 16, color: DVColors.ImputedData, marginRight: "4px" }),
                            React.createElement("span", null, tooltipData.imputed)))))))));
    };
    if (isLoading) {
        return (React.createElement("div", { style: { width }, className: "app-chart__heatmap" },
            labelComponent,
            React.createElement("div", { className: "app-chart__heatmap-skeleton", style: { width, height } })));
    }
    if (boxes.length === 0) {
        return (React.createElement("div", { style: { width }, className: "app-chart__heatmap" },
            labelComponent,
            React.createElement("div", { className: "app-chart__heatmap-box", style: { width, height } })));
    }
    const boxWidth = typeof width === 'number' ? width / boxes.length : 0;
    return (React.createElement("div", { style: { width }, className: "app-chart__heatmap" },
        labelComponent,
        boxes.map(({ score, tooltipData }, i) => (
        // eslint-disable-next-line react/no-array-index-key
        React.createElement(Tooltip, { key: `${label}-score-${score}-${i}`, title: createToolTip(i), placement: "right" },
            React.createElement(HeatMapBox, { className: "app-chart__heatmap-box", style: {
                    backgroundColor: score === null ? 'rgb(0, 0, 0, 0)' : getScoreColor(score)
                }, width: boxWidth || width, height: height, borderRight: (tooltipData.excluded && boxes[i + 1]?.tooltipData?.excluded) ||
                    i === boxes.length - 1
                    ? '0px'
                    : '2px solid #003' },
                tooltipData.dropped && React.createElement(HeatMapCrossedLine, { width: boxWidth - 2, height: height }),
                React.createElement(HeatMapInnerBox, { marginTop: tooltipData.dropped && -(1.6 * height), excluded: tooltipData.excluded || tooltipData.dropped })))))));
};
export default HeatMap;
